import React, { useState } from 'react';
import {
    Box,
    Divider
} from '@mui/material';
import TeamV2LeadDetailMenu from './TeamV2LeadDetailMenu';
import TeamV2LeadDetailCard from './TeamV2LeadDetailCard';
import TeamV2LeadDetailsEdit from './editLeadDetails/TeamV2LeadDetailsEdit';
import { EditOutlinedIcon, SaveAltOutlinedIcon, EmailOutlinedIcon } from 'icons/materialIcons';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import SendLeadEmailModal from 'components/contacts/contactDetails/SendLeadEmailModal';
import { useNavigate } from 'react-router-dom';
import { downloadVCard } from 'utils/vcfUtils';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';

const TeamV2LeadDetailsContainer = ({ lead, onBack, onDeleteComplete, startInEditMode = false }) => {
    const [isEditing, setIsEditing] = useState(startInEditMode);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [currentLead, setCurrentLead] = useState(lead);
    const navigate = useNavigate();

    // Mock data for team lead
    const mockLead = {
        _id: '1',
        leadName: 'Michael Rodriguez',
        leadEmail: 'michael.rodriguez@company.com',
        leadJobTitle: 'DevOps Engineer',
        leadCompany: 'Cloud Solutions',
        crmLink: 'https://crm.cloudsolutions.com/leads/12345',
        profilePictureUrl: 'https://i.pravatar.cc/150?img=1',
        cardId: 'mock-card-id-123', // Add cardId for email modal compatibility
        createdAt: '2025-09-16T10:30:00Z',
        exportedTo: 'Salesforce',
        cardSource: 'LinkedIn Import',
        bio: 'DevOps Engineer with 5 years experience in cloud infrastructure and automation'
    };

    const leadData = lead || mockLead;

    const handleUpdateLead = (updatedLead) => {
        // Update the current lead state with the new details
        setCurrentLead(updatedLead);
        // Exit edit mode
        setIsEditing(false);
    };

    // Function to handle edit click from any source
    const handleEditClick = () => {
        setIsEditing(true);
    };

    // Function to handle successful deletion
    const handleDeleteComplete = () => {
        // Navigate back to the leads list with a flag indicating deletion happened
        if (onDeleteComplete) {
            onDeleteComplete();
        } else {
            onBack();
        }
    };

    // Function to handle email click
    const handleEmailClick = () => {
        setIsEmailModalOpen(true);
    };

    // Function to prepare lead data for VCF download
    const prepareLeadData = () => {
        return {
            contact_info: {
                fullName: currentLead.leadName,
                email: currentLead.leadEmail,
                phoneNumber: currentLead.leadPhoneNumber,
                company: currentLead.leadCompany,
                jobTitle: currentLead.leadJobTitle,
                location: currentLead.location
            },
            bio: currentLead.notes,
            profilePictureUrl: currentLead.profilePictureUrl
        };
    };

    const actionButtons = [
        {
            icon: EditOutlinedIcon,
            text: "Edit",
            onClick: handleEditClick
        },
        {
            icon: SaveAltOutlinedIcon,
            text: "Save as contact",
            onClick: () => {
                // Prepare lead data for VCF download
                const leadData = prepareLeadData();
                downloadVCard(leadData);
            }
        },
        {
            icon: EmailOutlinedIcon,
            text: "Email Lead",
            onClick: handleEmailClick
        },
        {
            icon: LoopOutlinedIcon,
            text: "Reassign",
            onClick: () => {
                console.log('Reassign clicked');
            }
        }
    ];

    return (
        <Box sx={{
            width: '100%',
            height: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <TeamV2LeadDetailMenu
                lead={currentLead}
                onBack={onBack}
                onEditClick={handleEditClick}
                onDeleteComplete={handleDeleteComplete}
            />

            <Box sx={{
                bgcolor: '#ffffff',
                borderRadius: 4,
                m: 2,
                p: 0,
                display: 'flex',
                flex: 1,
                overflow: 'auto'
            }}>
                <Box sx={{
                    width: '100%',
                    p: 6,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {!isEditing && (
                        <Box sx={{
                            display: 'flex',
                            gap: 3,
                            mb: 2,
                            flexShrink: 0
                        }}>
                            {actionButtons.map((button, index) => (
                                <WhiteButtonWithIcon
                                    key={index}
                                    icon={button.icon}
                                    text={button.text}
                                    onClick={button.onClick || (() => console.log(`${button.text} clicked`))}
                                    padding="8px 48px"
                                    data-edit-lead-button={button.text === "Edit" ? true : undefined}
                                />
                            ))}
                        </Box>
                    )}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {isEditing ? (
                            <TeamV2LeadDetailsEdit
                                lead={currentLead}
                                onCancel={() => setIsEditing(false)}
                                onUpdate={handleUpdateLead}
                            />
                        ) : (
                            <TeamV2LeadDetailCard lead={currentLead} />
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Add the email modal */}
            <SendLeadEmailModal
                open={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                contact={currentLead}
            />
        </Box>
    );
};

export default TeamV2LeadDetailsContainer;